import {FACTIONS} from '../constants/factions';
import {GROUND_BACKGROUNDS} from './groundBackgrounds';
import {FACTION_UNIT_SOUND_FILES, getFactionUnitSoundFactionIds} from './factionUnitSounds';

/**
 * ОТЛОЖЕННЫЕ ЗВУКИ — звуки, без которых матч можно показать и начать.
 *
 * Основные звуки (scenes/sounds.js) грузятся вместе с картинками сцены, и пока
 * они не загружены, матч не открывается. Отложенные загрузку матча не держат:
 * их файлы качаются ПОСЛЕ основных ассетов, в фоне и по одному (см.
 * SoundManager). Пока звук не загружен, его воспроизведение молча
 * пропускается — без ошибок.
 *
 * Новый звук, который не должен задерживать загрузку матча, — это новая строка
 * здесь: ключ — имя звука, path — файл от корня public, volume — громкость.
 * Играет его SoundManager.playDeferredSound(имя).
 *
 * volume — множитель громкости файла; общий ползунок громкости игры
 * умножается на него. Множитель 1 — файл как есть.
 *
 * groundBackgrounds — подложки поля, на которых звук нужен: на остальных его
 * файл не качается вовсе. Без этого поля звук грузится в любой партии.
 *
 * factionIds — фракции, которым нужен звук: он качается, только если в партии
 * есть отряды этих фракций, нарисованные своим обликом (свои звуки отрядов —
 * configs/factionUnitSounds; там же их файлы, сюда они попадают сами).
 *
 * playerFactionIds — фракции, которым нужен звук независимо от облика: он
 * качается, только если в партии есть игрок одной из них (звук способности).
 *
 * isAwaited — звука ждет эффект на поле: пока файл не загружен, эффект не
 * показывается (вьюга, песчаный вихрь — см. scenes/FieldAmbience). Такой звук
 * не бросают после обычных попыток: его пробуют снова и снова, с долгой
 * паузой, — иначе на слабой связи эффекта не было бы до конца партии.
 *
 * requestTimeoutMs — сколько ждать этот файл, если он велик и общий предел
 * (DEFERRED_SOUNDS_LOADING.requestTimeoutMs) ему мал.
 */
// Подложки, к которым привязаны звуки погоды: зимние (вьюга, поземка), луга и
// плато (ветер в траве), пустыни (ветер над песком), солончак
const WINTER = Object.freeze([
  GROUND_BACKGROUNDS.WINTER_1, GROUND_BACKGROUNDS.WINTER_2, GROUND_BACKGROUNDS.WINTER_3,
  GROUND_BACKGROUNDS.WINTER_4, GROUND_BACKGROUNDS.WINTER_5,
]);
const MEADOWS = Object.freeze([
  GROUND_BACKGROUNDS.GREEN_1, GROUND_BACKGROUNDS.GREEN_2, GROUND_BACKGROUNDS.GREEN_3, GROUND_BACKGROUNDS.GREEN_4,
  // На плато ветер несет вуали пыли над сухой травой — шелест тот же
  GROUND_BACKGROUNDS.PLATO_1,
  // На острове по сухой траве идет морской бриз — та же травяная волна, что на лугах
  GROUND_BACKGROUNDS.ISLAND_1,
]);
const DESERTS = Object.freeze([GROUND_BACKGROUNDS.DESERT_1, GROUND_BACKGROUNDS.DESERT_2]);
// «Волшебный» ветер: вуали соляной пыли и светящиеся лабиринты спиралей
const MAGIC_WIND_GROUNDS = Object.freeze([GROUND_BACKGROUNDS.SALT_1, GROUND_BACKGROUNDS.SPIRAL_1]);
const GEYSERS = Object.freeze([GROUND_BACKGROUNDS.GEYSER_1]);
const GOBLINS_ONLY = Object.freeze([FACTIONS.GOBLINS]);
const LEPRECHAUNS_ONLY = Object.freeze([FACTIONS.LEPRECHAUNS]);
const SUCCUBES_ONLY = Object.freeze([FACTIONS.SUCCUBES]);

export const DEFERRED_SOUNDS = Object.freeze({
  // Голоса птиц над полем — тихие, фоном (кому какой голос — см.
  // configs/fieldAmbienceSounds). ГРОМКОСТЬ КАЖДОГО ГОЛОСА — его volume.
  //
  // Сами файлы записаны с очень разной громкостью (клекот орла на слух
  // вчетверо громче дальних чаек), поэтому и множители разные: они
  // выравнивают голоса до одного тихого уровня — заметно тише обычных звуков
  // игры. Одинаковые множители дали бы орла в полный голос и неслышных чаек.
  // Хочешь все голоса тише или громче — меняй их в одну и ту же сторону
  birds_crows_squawking: Object.freeze({path: '/sounds/birds/crows-squawking.mp3', volume: 0.12}),
  birds_flock_of_crows: Object.freeze({path: '/sounds/birds/flock-of-crows-squawking.mp3', volume: 0.23}),
  birds_seagulls_calling: Object.freeze({path: '/sounds/birds/seagulls-calling-angrily.mp3', volume: 0.3}),
  birds_seagulls_in_flight: Object.freeze({path: '/sounds/birds/seagulls-in-flight.mp3', volume: 0.5}),
  birds_eagle_screech: Object.freeze({path: '/sounds/birds/bald-eagle-screech.mp3', volume: 0.06}),
  birds_hawk_in_flight: Object.freeze({path: '/sounds/birds/hawk-in-flight.mp3', volume: 0.08}),
  // Вой вьюги — звучит, пока над зимним полем идет снежный заряд (см.
  // configs/biomeEffects/snowDrift и scenes/FieldAmbience). Нужен только на
  // зимних подложках — только на них и качается. Пока он не загружен, вьюга на
  // поле не начинается: звук и картинка идут только вместе.
  // Файл — 45 секунд (заряд идет до 36), 128 кбит/с, 0.7 МБ: вырезан из
  // полутораминутной записи в 3.6 МБ (исходник — developer_artifacts/
  // art_sources/field_ambience/sounds). Он все же в разы больше голосов птиц,
  // и на слабой связи за общие 20 секунд не приходит — у файла свой предел
  storm_icy_wind: Object.freeze({
    path: '/sounds/icy-snowstorm-wind.mp3',
    volume: 0.5,
    groundBackgrounds: WINTER,
    isAwaited: true,
    requestTimeoutMs: 60000,
  }),
  // Ветер песчаной бури — звучит, пока по красному каньону идет пыльный вихрь
  // (см. configs/biomeEffects/dustDevil). Правила те же, что у воя вьюги:
  // качается только на своей подложке, и пока не загружен, вихрь не появляется.
  // Запись (33 секунды, 128 кбит/с) громче воя вьюги на 5 дБ — множитель
  // меньше, чтобы на слух они были вровень
  storm_desert_wind: Object.freeze({
    path: '/sounds/desert-wind-sandstorm.mp3',
    volume: 0.6,
    groundBackgrounds: Object.freeze([GROUND_BACKGROUNDS.RED_1]),
    isAwaited: true,
    requestTimeoutMs: 60000,
  }),
  // Свист ветра в поземке — звучит один из них, когда порыв поземки попадает в
  // кадр игрока (см. configs/fieldEffectSounds). Порыва звук не держит: не
  // загружен — порыв пройдет молча. Записи короткие (5–6 секунд), пережаты до
  // 128 кбит/с (исходники — developer_artifacts/art_sources/field_ambience/sounds)
  wind_snow_1: Object.freeze({path: '/sounds/snow_wind/winter-wind-whistle-1.mp3', volume: 0.45, groundBackgrounds: WINTER}),
  wind_snow_2: Object.freeze({path: '/sounds/snow_wind/winter-wind-whistle-2.mp3', volume: 0.45, groundBackgrounds: WINTER}),
  wind_snow_3: Object.freeze({path: '/sounds/snow_wind/winter-wind-whistle-3.mp3', volume: 0.45, groundBackgrounds: WINTER}),
  // Шелест ветра в траве — звучит один из них, когда порыв ветра над лугом
  // (или вуаль пыли над лугом и плато) попадает в кадр игрока. Правила те же,
  // что у свиста поземки. Исходные
  // записи были очень тихими (на 14 и 18 дБ тише свиста поземки): при
  // пережатии подняты до его уровня, чтобы множители громкости были сравнимы
  wind_grass_1: Object.freeze({path: '/sounds/grass_wind/grass_wind_1.mp3', volume: 0.55, groundBackgrounds: MEADOWS}),
  wind_grass_2: Object.freeze({path: '/sounds/grass_wind/grass_wind_2.mp3', volume: 0.7, groundBackgrounds: MEADOWS}),
  // Ветер над песком — звучит, когда песчаные змейки пустыни попадают в кадр
  // игрока. Правила те же, что у свиста поземки; запись тоже была очень тихой
  // и при пережатии поднята на 13 дБ
  wind_sand_1: Object.freeze({path: '/sounds/sand_wind/sand_wind_1.mp3', volume: 0.35, groundBackgrounds: DESERTS}),
  // «Волшебный» ветер солончака — звучит один из них, когда вуаль соляной пыли
  // попадает в кадр игрока. Правила те же, что у свиста поземки
  wind_magic_1: Object.freeze({path: '/sounds/magic_wind/magic_wind_1.mp3', volume: 0.4, groundBackgrounds: MAGIC_WIND_GROUNDS}),
  wind_magic_2: Object.freeze({path: '/sounds/magic_wind/magic_wind_2.mp3', volume: 0.4, groundBackgrounds: MAGIC_WIND_GROUNDS}),
  wind_magic_3: Object.freeze({path: '/sounds/magic_wind/magic_wind_3.mp3', volume: 0.35, groundBackgrounds: MAGIC_WIND_GROUNDS}),
  // Шипение пара — звучит одно из них, когда в кадре игрока гейзер выпускает
  // пар или воду: извергается нарисованный гейзер у края поля или из трещины
  // на самом поле поднимается струйка пара. Записи короткие (полторы-две с
  // половиной секунды). Правила загрузки те же, что у ветров
  steam_1: Object.freeze({path: '/sounds/steam_released/steam_sizzle_1.mp3', volume: 0.3, groundBackgrounds: GEYSERS}),
  steam_2: Object.freeze({path: '/sounds/steam_released/steam_sizzle_2.mp3', volume: 0.3, groundBackgrounds: GEYSERS}),
  steam_3: Object.freeze({path: '/sounds/steam_released/steam_sizzle_3.mp3', volume: 0.3, groundBackgrounds: GEYSERS}),
  steam_4: Object.freeze({path: '/sounds/steam_released/steam_sizzle_4.mp3', volume: 0.3, groundBackgrounds: GEYSERS}),
  // Прожигание захваченного города в облете конца раунда (см.
  // scenes/CityCaptureAnimation): чем больше очков стоит город, тем мощнее звук
  // (какой звук какому городу — CITY_CAPTURE_ANIMATION.burnSounds). Записи
  // 2.3 / 2.3 / 3.7 секунды с пиком на первой секунде — под огненный фронт
  // (2.2 секунды); пережаты до 128 кбит/с (исходники — developer_artifacts/
  // art_sources/city_capture/sounds). Записи громкие (-14 / -13 / -11 LUFS);
  // множитель 1 — как файл: на 0.6 прожигание терялось среди звуков поля.
  // Крупный город и сам по себе звучит громче — так и задумано
  city_capture_burn_small: Object.freeze({path: '/sounds/city_capture/city_capture_whoosh_1.mp3', volume: 1}),
  city_capture_burn_medium: Object.freeze({path: '/sounds/city_capture/city_capture_whoosh_2.mp3', volume: 1}),
  city_capture_burn_large: Object.freeze({path: '/sounds/city_capture/city_capture_whoosh_3.mp3', volume: 1}),
  // Рост дохода руин (см. scenes/RuinsIncomeAnimation): нарастание, пока камера
  // отъезжает, удар первой вспышки и колокольчик следующих руин. Записи с
  // Uppbeat (авторы и исходники — developer_artifacts/art_sources/ruins_income/
  // sounds); множитель 1 — как файл, громкость выровнена при нарезке
  ruins_income_riser: Object.freeze({path: '/sounds/ruins_income/ruins_income_riser.mp3', volume: 1}),
  ruins_income_impact: Object.freeze({path: '/sounds/ruins_income/ruins_income_impact.mp3', volume: 1}),
  ruins_income_strike: Object.freeze({path: '/sounds/ruins_income/ruins_income_strike.mp3', volume: 0.7}),
  // Способности фракций (см. scenes/playAbilityCastEffects): заклинание звучит
  // с вылетом сердца, бомбы или блесток, итог — в момент попадания. Записи с
  // Uppbeat (авторы и исходники — developer_artifacts/art_sources/
  // ability_sounds), обрезаны до 1.2–2.7 секунды и выровнены по громкости:
  // заклинания тише (-19 LUFS), итоги — на уровне звуков поля, взрыв нарочно
  // громче (-11 LUFS, основа — попадание снаряда катапульты).
  // Пока файл не загружен, итог звучит прежним общим звуком (см. SoundManager)
  ability_succubes_charm_cast: Object.freeze({path: '/sounds/abilities/succubes_charm_cast.mp3', volume: 0.9, playerFactionIds: SUCCUBES_ONLY}),
  ability_succubes_charm_success: Object.freeze({path: '/sounds/abilities/succubes_charm_success.mp3', volume: 1, playerFactionIds: SUCCUBES_ONLY}),
  ability_succubes_charm_demon: Object.freeze({path: '/sounds/abilities/succubes_charm_demon.mp3', volume: 1, playerFactionIds: SUCCUBES_ONLY}),
  // Агония отряда, погибшего от голода: у этой гибели нет модалки с ее криками
  unit_starvation_agony: Object.freeze({path: '/sounds/abilities/unit_starvation_agony.mp3', volume: 1}),
  ability_succubes_charm_failure: Object.freeze({path: '/sounds/abilities/succubes_charm_failure.mp3', volume: 0.9, playerFactionIds: SUCCUBES_ONLY}),
  ability_goblins_bomb_throw: Object.freeze({path: '/sounds/abilities/goblins_bomb_throw.mp3', volume: 0.8, playerFactionIds: GOBLINS_ONLY}),
  // Взрыв — без привязки к фракции: он же звучит при найме отрядов (см. SoundManager)
  ability_goblins_bomb_explosion: Object.freeze({path: '/sounds/abilities/goblins_bomb_explosion.mp3', volume: 1}),
  ability_unit_upgrade_magic: Object.freeze({path: '/sounds/abilities/unit_upgrade_magic.mp3', volume: 1}),
  ability_leprechauns_theft_magic: Object.freeze({path: '/sounds/abilities/leprechauns_theft_magic.mp3', volume: 0.9}),
  ability_goblins_trap_cast: Object.freeze({path: '/sounds/abilities/goblins_trap_cast.mp3', volume: 0.8, playerFactionIds: GOBLINS_ONLY}),
  // Основание города (центральный квартал): рог поверх удара постройки
  building_city_founded_horn: Object.freeze({path: '/sounds/abilities/city_founded_horn.mp3', volume: 0.75}),
  // Смешок гоблина при успехе ловушки и саботажа: те же записи, что у выбора
  // отряда в облике фракции, но отдельными звуками — те качаются только при
  // облике, а гоблин смеется и без него (кому какой — resolveGoblinsSuccessSoundId)
  ability_goblins_success_d6: Object.freeze({path: '/sounds/units/goblins_d6.mp3', volume: 1, playerFactionIds: GOBLINS_ONLY}),
  ability_goblins_success_d10: Object.freeze({path: '/sounds/units/goblins_d10.mp3', volume: 1, playerFactionIds: GOBLINS_ONLY}),
  ability_goblins_success_d20: Object.freeze({path: '/sounds/units/goblins_d20.mp3', volume: 1, playerFactionIds: GOBLINS_ONLY}),
  // Смех лепрекона при краже монет: так же записи выбора отряда отдельными
  // звуками — лепрекон смеется и без облика (кому какой — resolveLeprechaunsTheftLaughSoundId)
  ability_leprechauns_laugh_d6: Object.freeze({path: '/sounds/units/leprechauns_d6.mp3', volume: 1, playerFactionIds: LEPRECHAUNS_ONLY}),
  ability_leprechauns_laugh_d10: Object.freeze({path: '/sounds/units/leprechauns_d10.mp3', volume: 1, playerFactionIds: LEPRECHAUNS_ONLY}),
  ability_leprechauns_laugh_d20: Object.freeze({path: '/sounds/units/leprechauns_d20.mp3', volume: 1, playerFactionIds: LEPRECHAUNS_ONLY}),
  // Свои звуки отрядов фракций (клич, голос зверя, шаг) — перечень и кому
  // какой звучит — в configs/factionUnitSounds
  ...Object.fromEntries(Object.entries(FACTION_UNIT_SOUND_FILES).map(([soundId, sound]) => [
    soundId,
    Object.freeze({...sound, factionIds: Object.freeze(getFactionUnitSoundFactionIds(soundId))}),
  ])),
});

/** Как грузятся отложенные звуки */
export const DEFERRED_SOUNDS_LOADING = Object.freeze({
  // Сколько ждать после загрузки основных ассетов, мс: матч уже показан, и
  // фоновая загрузка не спорит с его первыми кадрами
  startDelayMs: 2000,
  // Пауза между файлами, мс: звуки качаются по одному, а не всей пачкой
  gapMs: 250,
  // Сколько ждать один файл, мс: зависший запрос обрывается и не держит остальные
  requestTimeoutMs: 20000,
  // Не загрузившиеся звуки пробуем снова: всего проходов по перечню и пауза
  // перед повторным, мс
  maxAttempts: 3,
  retryDelayMs: 15000,
  // Звук, которого ждет эффект на поле (isAwaited), пробуем и дальше: столько
  // проходов сверх обычных, с такой паузой, мс. Двадцать минут — и хватит:
  // файла, видимо, нет вовсе
  maxLateAttempts: 20,
  lateRetryDelayMs: 60000,
});

/**
 * Нужен ли отложенный звук на этой подложке поля и при этих фракциях
 * @param {string} soundId
 * @param {string|null} [groundBackgroundId]
 * @param {string[]} [skinFactionIds] — фракции, чьи отряды в партии нарисованы
 *   особым образом: им нужны свои звуки отрядов (factionIds у звука)
 * @param {string[]} [playerFactionIds] — фракции всех игроков партии, с обликом
 *   или без него (playerFactionIds у звука)
 * @returns {boolean} false — такого звука нет или в этой партии он не нужен
 */
export const checkIfDeferredSoundNeeded = (soundId, groundBackgroundId, skinFactionIds = [], playerFactionIds = []) => {
  const sound = Object.prototype.hasOwnProperty.call(DEFERRED_SOUNDS, soundId) ? DEFERRED_SOUNDS[soundId] : null;
  if (!sound) {
    return false;
  }
  if (sound.groundBackgrounds && !sound.groundBackgrounds.includes(groundBackgroundId)) {
    return false;
  }
  if (sound.playerFactionIds && !sound.playerFactionIds.some((factionId) => playerFactionIds.includes(factionId))) {
    return false;
  }
  return !sound.factionIds || sound.factionIds.some((factionId) => skinFactionIds.includes(factionId));
};

/**
 * Настройки отложенного звука
 * @param {string} soundId
 * @returns {{path: string, volume: number, groundBackgrounds?: string[], factionIds?: string[],
 *   playerFactionIds?: string[], isAwaited?: boolean, requestTimeoutMs?: number}|null} null — такого звука нет
 */
export const getDeferredSound = (soundId) => (
  Object.prototype.hasOwnProperty.call(DEFERRED_SOUNDS, soundId) ? DEFERRED_SOUNDS[soundId] : null
);
